import { useState, useRef, useCallback, type RefObject, type ChangeEvent } from 'react'
import { supabase } from '../../../lib/supabase'
import {
    FIELD_LIMITS,
    truncateField,
    normalizeToArray,
    validateAuthors,
    validateCategories,
} from '../../../utils/validation'
import { useFileImport } from '../../../hooks/useFileImport'
import { formatAuthors, formatCategories } from '../formattingUtils'
import type { Book, FieldVisibility, ViewMode } from '@/types'

/**
 * Imported book with metadata
 */
interface ImportedBook extends Partial<Book> {
    id: string
    status: 'exact' | 'partial' | 'new'
}

/**
 * Notification function type
 */
type ShowNotifyFn = (title: string, message: string, type: 'success' | 'danger' | 'warning') => void

/**
 * Parameters for useBookExport hook
 */
interface UseBookExportParams {
    books: Book[]
    loadBooks: () => Promise<void>
    showNotify: ShowNotifyFn
}

/**
 * Return type for useBookExport hook
 */
interface UseBookExportReturn {
    // State
    importData: ImportedBook[]
    showImportPreview: boolean
    setShowImportPreview: (show: boolean) => void
    isImporting: boolean
    showExportOptions: boolean
    setShowExportOptions: (show: boolean) => void
    fileInputRef: RefObject<HTMLInputElement>

    // Import functions
    handleImportClick: () => void
    handleFileUpload: (e: ChangeEvent<HTMLInputElement>) => void
    handleImportConfirm: () => Promise<void>
    removeImportItem: (id: string) => void
    updateImportItem: (id: string, field: string, value: unknown) => void

    // Export functions
    handleExport: (
        format: 'csv' | 'json',
        viewMode: ViewMode,
        exportBooks: Book[],
        effectiveListColumns: FieldVisibility
    ) => void
}

/**
 * Custom hook for book export/import functionality
 * 
 * Handles CSV and JSON export with view-specific logic, import with duplicate detection
 */
export function useBookExport({ books, loadBooks, showNotify }: UseBookExportParams): UseBookExportReturn {
    const { parseFile } = useFileImport()

    // State
    const [importData, setImportData] = useState<ImportedBook[]>([])
    const [showImportPreview, setShowImportPreview] = useState(false)
    const [isImporting, setIsImporting] = useState(false)
    const [showExportOptions, setShowExportOptions] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    /**
     * Check if imported book is duplicate
     */
    const checkDuplicate = useCallback(
        (book: Partial<Book>): 'exact' | 'partial' | 'new' => {
            const bookTitle = book.title?.toLowerCase().trim()
            const importAuthors = (book.author || []).map((a) => a?.toLowerCase().trim())

            const exactMatch = books.find((b) => {
                const existingTitle = b.title?.toLowerCase().trim()
                if (existingTitle !== bookTitle) return false

                const existingAuthors = (b.author || []).map((a) => a?.toLowerCase().trim())
                return existingAuthors.some((ea) => importAuthors.includes(ea))
            })
            if (exactMatch) return 'exact'

            const titleMatch = books.find((b) => b.title?.toLowerCase().trim() === bookTitle)
            if (titleMatch) return 'partial'

            return 'new'
        },
        [books]
    )

    /**
     * Open file picker for import
     */
    const handleImportClick = useCallback(() => {
        fileInputRef.current?.click()
    }, [])

    /**
     * Handle file upload event
     */
    const handleFileUpload = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file) return

            const reader = new FileReader()
            reader.onload = (evt) => {
                const content = evt.target?.result as string
                const fileName = file.name.toLowerCase()
                const type = fileName.endsWith('.json') ? 'json' : 'csv'

                if (!fileName.endsWith('.json') && !fileName.endsWith('.csv')) {
                    showNotify('Formato no soportado', 'Solo se permiten archivos CSV y JSON.', 'warning')
                    return
                }

                try {
                    const parsedBooks = parseFile(content, type)
                    // Add temp IDs and duplicate status, convert numeric fields
                    const dataWithMeta: ImportedBook[] = parsedBooks.map((book, idx) => {
                        // Convert string numbers to actual numbers
                        const pageCount = typeof book.page_count === 'string'
                            ? parseInt(book.page_count, 10) || undefined
                            : book.page_count

                        const seriesNumber = typeof book.series_number === 'string'
                            ? parseInt(book.series_number, 10) || undefined
                            : book.series_number

                        const converted: ImportedBook = {
                            ...book,
                            page_count: pageCount,
                            series_number: seriesNumber,
                            id: `temp-${Date.now()}-${idx}`,
                            status: checkDuplicate({ ...book, page_count: pageCount, series_number: seriesNumber }),
                        }
                        return converted
                    })

                    if (dataWithMeta.length > 0) {
                        setImportData(dataWithMeta)
                        setShowImportPreview(true)
                    }
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Error desconocido'
                    showNotify('Error de Importación', message, 'danger')
                }
                e.target.value = ''
            }
            reader.readAsText(file)
        },
        [parseFile, checkDuplicate, showNotify]
    )

    /**
     * Confirm and execute import
     */
    const handleImportConfirm = useCallback(async () => {
        setIsImporting(true)
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser()
            if (!user) throw new Error('No authenticated user found')

            const b = FIELD_LIMITS.books
            const toInsert = importData.map((item) => ({
                user_id: user.id,
                title: item.title,
                author: validateAuthors(item.author),
                category: validateCategories(item.category),
                notes: truncateField(item.notes, b.notes),
                synopsis: truncateField(item.synopsis, b.synopsis),
                description: truncateField(item.description, b.description),
                publisher: truncateField(item.publisher, b.publisher),
                location: truncateField(item.location, b.location),
                series: truncateField(item.series, b.series),
                series_number: item.series_number,
                language: truncateField(item.language, b.language),
                format: item.format,
                status: item.status,
                progress: item.progress,
            }))

            const { error } = await supabase.from('books').insert(toInsert)
            if (error) throw error

            await loadBooks()
            setShowImportPreview(false)
            setImportData([])
            showNotify('Importación Exitosa', `Se han importado ${toInsert.length} libros correctamente.`, 'success')
        } catch (err) {
            console.error('Error importing books:', err)
            showNotify('Error', 'Hubo un error al guardar los libros.', 'danger')
        } finally {
            setIsImporting(false)
        }
    }, [importData, loadBooks, showNotify])

    /**
     * Remove item from import preview
     */
    const removeImportItem = useCallback((id: string) => {
        setImportData((prev) => prev.filter((item) => item.id !== id))
    }, [])

    /**
     * Update field in import preview item
     */
    const updateImportItem = useCallback(
        (id: string, field: string, value: unknown) => {
            setImportData((prev) =>
                prev.map((item) => {
                    if (item.id === id) {
                        let finalValue = value
                        const b = FIELD_LIMITS.books

                        // Apply validation and truncation
                        if (field === 'author') finalValue = validateAuthors(normalizeToArray(value as string | string[]))
                        else if (field === 'category')
                            finalValue = validateCategories(normalizeToArray(value as string | string[]))
                        else if (field === 'notes') finalValue = truncateField(value as string, b.notes)
                        else if (field === 'synopsis') finalValue = truncateField(value as string, b.synopsis)
                        else if (field === 'description') finalValue = truncateField(value as string, b.description)
                        else if (field === 'publisher') finalValue = truncateField(value as string, b.publisher)
                        else if (field === 'location') finalValue = truncateField(value as string, b.location)
                        else if (field === 'series') finalValue = truncateField(value as string, b.series)
                        else if (field === 'language') finalValue = truncateField(value as string, b.language)

                        const updated = { ...item, [field]: finalValue }
                        return { ...updated, status: checkDuplicate(updated) }
                    }
                    return item
                })
            )
        },
        [checkDuplicate]
    )

    /**
     * Export books to CSV or JSON
     */
    const handleExport = useCallback(
        (format: 'csv' | 'json', viewMode: ViewMode, exportBooks: Book[], effectiveListColumns: FieldVisibility) => {
            let dataToExport: Record<string, unknown>[] | Book[] = exportBooks

            // Apply view-specific logic
            if (viewMode === 'compact-list') {
                dataToExport = exportBooks.map((b) => ({
                    title: b.title,
                    author: formatAuthors(b.author),
                }))
            } else if (viewMode === 'extended-list') {
                dataToExport = exportBooks.map((b) => {
                    const exported: Record<string, unknown> = {}
                    if (effectiveListColumns.title) exported.title = b.title
                    if (effectiveListColumns.author) exported.author = formatAuthors(b.author)
                    if (effectiveListColumns.language) exported.language = b.language
                    if (effectiveListColumns.category) exported.category = formatCategories(b.category)
                    if (effectiveListColumns.publisher) exported.publisher = b.publisher
                    if (effectiveListColumns.series) exported.series = b.series
                    if (effectiveListColumns.series_number) exported.series_number = b.series_number
                    if (effectiveListColumns.format) exported.format = b.format
                    if (effectiveListColumns.status) exported.status = b.status
                    if (effectiveListColumns.progress) exported.progress = b.progress
                    if (effectiveListColumns.location) exported.location = b.location
                    return exported
                })
            }

            if (format === 'csv') {
                const firstRow = dataToExport[0] || {}
                const headers = Object.keys(firstRow)
                const headerNames = new Map([
                    ['title', 'Título'],
                    ['author', 'Autor'],
                    ['language', 'Idioma'],
                    ['category', 'Categoría'],
                    ['publisher', 'Editorial'],
                    ['series', 'Serie'],
                    ['series_number', 'Número Serie'],
                    ['format', 'Formato'],
                    ['status', 'Estado'],
                    ['progress', 'Progreso'],
                    ['location', 'Ubicación'],
                ])
                const translatedHeaders = headers.map((h) => headerNames.get(h) || h)

                const csvContent = [
                    translatedHeaders.join(','),
                    ...dataToExport.map((row) =>
                        headers
                            .map((h) => {
                                const val = Object.prototype.hasOwnProperty.call(row, h) ? row[h] : ''
                                return `"${String(val).replace(/"/g, '""')}"`
                            })
                            .join(',')
                    ),
                ].join('\n')

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `bibliotelia_export_${new Date().toISOString().split('T')[0]}.csv`
                link.click()
            } else if (format === 'json') {
                const jsonContent = JSON.stringify(dataToExport, null, 2)
                const blob = new Blob([jsonContent], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `biblioteca_${new Date().toISOString().split('T')[0]}.json`
                a.click()
            }

            setShowExportOptions(false)
        },
        []
    )

    return {
        // State
        importData,
        showImportPreview,
        setShowImportPreview,
        isImporting,
        showExportOptions,
        setShowExportOptions,
        fileInputRef,

        // Import functions
        handleImportClick,
        handleFileUpload,
        handleImportConfirm,
        removeImportItem,
        updateImportItem,

        // Export functions
        handleExport,
    }
}
